/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-runloop, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { action, computed } from '@ember/object';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal Imports
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import { tablePrintCss } from 'linced-app/utils/table';
import moment from 'moment/moment';
import { compact, findBy } from '@linc-technologies/linced-common/utils/arrays';

const SCHOOL_LISTS = [
  {
    key: 'med-room-medical',
    label: 'Medical Room Report (Students with Medical and/or Medication only)',
  },
  { key: 'med-room-all', label: 'Medical Room Report (All students)' },
  {
    key: 'directory-primary',
    label: 'Full School Directory (Primary Contacts only)',
  },
  { key: 'directory-all', label: 'Full School Directory (All Contacts)' },
  { key: 'daily-roll', label: 'Daily Roll' },
  { key: 'weekly-roll', label: 'Weekly Roll' },
  {
    key: 'perm-opts-paid',
    label: 'Permissions and Options Report (including Paid column)',
  },
  {
    key: 'perm-opts-unpaid',
    label: 'Permissions and Options Report (excluding Paid)',
  },
  { key: 'generic-blank', label: 'Generic List with Blank Columns' },
  { key: 'staff-generic', label: 'Staff Generic List' },
];

const CLASS_LISTS = [
  {
    key: 'med-room-medical',
    label: 'Medical Room Report (Students with Medical and/or Medication only)',
  },
  { key: 'med-room-all', label: 'Medical Room Report (All students)' },
  {
    key: 'directory-primary',
    label: 'Full School Directory (Primary Contacts only)',
  },
  { key: 'directory-all', label: 'Full School Directory (All Contacts)' },
  { key: 'daily-roll', label: 'Daily Roll' },
  { key: 'weekly-roll', label: 'Weekly Roll' },
  {
    key: 'perm-opts-paid',
    label: 'Permissions and Options Report (including Paid column)',
  },
  {
    key: 'perm-opts-unpaid',
    label: 'Permissions and Options Report (excluding Paid)',
  },
  { key: 'generic-blank', label: 'Generic List with Blank Columns' },
];

export default class PermissionReplyReports extends Component {
  @service
  dates;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  EnrolmentStatus = EnrolmentStatus;
  SCHOOL_LISTS = SCHOOL_LISTS;
  CLASS_LISTS = CLASS_LISTS;
  event = null;
  learners = null;

  @tracked
  homerooms = null;

  groupMembers = null;

  @computed('homerooms.@each.name', 'learners.[]')
  get homeroomLearners() {
    if (isEmpty(this.homerooms) || isEmpty(this.learners)) {
      return [];
    }

    return this.homerooms
      .slice()
      .sort(naturalSort('name'))
      .map((homeroom) => {
        return {
          homeroom,
          learners: compact(
            homeroom.currentLearners.map((learner) => {
              return findBy(this.learners, 'id', learner.personId);
            }),
          ),
        };
      })
      .filter((data) => {
        return !isEmpty(data.learners);
      });
  }

  @tracked
  dialogOpen = false;

  @tracked
  printing = false;

  @tracked
  schoolBlankColumn = 1;

  @action
  setSchoolBlankColumn(value) {
    this.schoolBlankColumn = value;
  }

  @tracked
  schoolIncludePhotoIncludingPaid = false;

  @action
  setSchoolIncludePhotoIncludingPaid(value) {
    this.schoolIncludePhotoIncludingPaid = value;
  }

  @tracked
  schoolIncludePhotoExcludingPaid = false;

  @action
  setSchoolIncludePhotoExcludingPaid(value) {
    this.schoolIncludePhotoExcludingPaid = value;
  }

  @tracked
  schoolListSelection = [];

  @tracked
  schoolSort = 'lastName';

  @action
  setSchoolSort(value) {
    this.schoolSort = value;
  }

  @tracked
  classBlankColumn = 1;

  @action
  setClassBlankColumn(value) {
    this.classBlankColumn = value;
  }

  @tracked
  classIncludePhotoIncludingPaid = false;

  @action
  setClassIncludePhotoIncludingPaid(value) {
    this.classIncludePhotoIncludingPaid = value;
  }

  @tracked
  classIncludePhotoExcludingPaid = false;

  @action
  setClassIncludePhotoExcludingPaid(value) {
    this.classIncludePhotoExcludingPaid = value;
  }

  @tracked
  classListSelection = [];

  @tracked
  classSort = 'lastName';

  @action
  setClassSort(value) {
    this.classSort = value;
  }

  date = moment().format('YYYY-MM-DD');

  @tracked
  orientation = 'landscape';

  @action
  setOrientation(value) {
    this.orientation = value;
  }

  reset() {
    this.schoolListSelection = [];
    this.classListSelection = [];
    this.schoolBlankColumn = 1;
    this.schoolIncludePhotoIncludingPaid = false;
    this.schoolIncludePhotoExcludingPaid = false;
    this.schoolSort = 'lastName';
    this.classBlankColumn = 1;
    this.classIncludePhotoIncludingPaid = false;
    this.classIncludePhotoExcludingPaid = false;
    this.classSort = 'lastName';
    this.printing = false;
    this.orientation = 'landscape';
  }

  closeDialog() {
    this.dialogOpen = false;
  }

  @action
  openDialog() {
    this.store
      .findAll('medical/medication')
      .then(() => {
        this.store
          .query('group', {
            year: moment().format('YYYY'),
            groupType: GroupType.HOMEROOM,
          })
          .then((groups) => {
            this.reset();
            this.dialogOpen = true;
            this.homerooms = groups;
          })
          .catch(() => {
            this.toaster.error('Error loading homerooms!');
          });
      })
      .catch(() => {
        this.toaster.error('Error loading medications!');
      });
  }

  @action
  addSchoolListSelection(list) {
    if (this.schoolListSelection.includes(list)) {
      this.schoolListSelection = this.schoolListSelection.filter(
        (l) => l !== list,
      );
    } else {
      this.schoolListSelection = [...this.schoolListSelection, list];
    }
  }

  @action
  addClassListSelection(list) {
    if (this.classListSelection.includes(list)) {
      this.classListSelection = this.classListSelection.filter(
        (l) => l !== list,
      );
    } else {
      this.classListSelection = [...this.classListSelection, list];
    }
  }

  @action
  print() {
    this.printing = true;
    if (!this.element) {
      this.toaster.error('Element not found!');
      this.closeDialog();
      return;
    }
    let reportDiv = this.element.querySelector('div.report');
    let printer = this.element.querySelector('iframe');
    if (!reportDiv || !printer || !printer.contentDocument) {
      this.toaster.error('Print elements not found!');
      this.closeDialog();
      return;
    }
    let printHTML = reportDiv.innerHTML;
    printer.contentDocument.title = this.event.title;
    printer.contentDocument.body.className = 'report-print';
    let customCss = `
    <style>
      .page-break {
        page-break-before: always;
      }
      @media print {
        @page {
          size: ${this.orientation};
        }
      }
      .generic .content {
        width: 1%;
        white-space: nowrap;
      }
      .generic .fill {
        width: auto;
      }

    </style>`;

    printer.contentDocument.body.innerHTML =
      tablePrintCss + customCss + printHTML;

    // Trigger the actual printing ~500ms after content insertion.
    later(printer.contentWindow, 'print', 1500);

    // Delay slightly longer before closing and resetting, to avoid a
    // race condition which sometimes closes the dialog without printing.
    later(this, 'closeDialog', 2000);
  }
}
